/**
 * Client HTTP verso l'API.
 *
 * Due responsabilità che vale la pena tenere qui e non nei componenti:
 *  - il token CSRF: sta in un cookie leggibile e va rimandato nell'header su
 *    ogni richiesta mutante;
 *  - la traduzione degli errori: l'API restituisce codici, la UI mostra frasi
 *    in italiano senza dettagli tecnici (spec §9).
 */
import type {
  AdminClient,
  AdminClientsResult,
  AuditFilters,
  AuditMeta,
  AuditPage,
  BrowseResult,
  ClientInput,
  ClientRulesResult,
  FoldersResult,
  LinkTarget,
  Me,
  OrgUsersResult,
  PortalUsersResult,
  PreviewResult,
  SearchResponse,
  ResolveUserResult,
  Rule,
  RuleInput,
  Template,
  UploadSessionInfo,
  UserRulesResult,
} from './types.js';

const CSRF_COOKIE = 'mm_csrf';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Messaggi utente. Quelli dell'API sono già in italiano: qui si copre il resto. */
const MESSAGES: Record<string, string> = {
  unauthorized: 'Sessione scaduta. Effettua di nuovo l\'accesso.',
  forbidden: 'Operazione non consentita.',
  not_found: 'Elemento non trovato. Potrebbe essere stato spostato.',
  conflict: 'Elemento già presente.',
  payload_too_large: 'File troppo grande.',
  unsupported_media_type: 'Tipo di file non ammesso.',
  too_many_requests: 'Troppe richieste. Attendi qualche minuto.',
  upstream_unavailable: 'Servizio temporaneamente non disponibile. Riprova fra poco.',
  internal: 'Errore interno. Se il problema persiste contatta l\'assistenza.',
  network: 'Connessione non disponibile. Controlla la rete e riprova.',
};

export function readCsrfToken(): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'internal';
  let message = '';
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    code = body.error?.code ?? code;
    message = body.error?.message ?? '';
  } catch {
    // Risposta non JSON: si resta sul messaggio generico.
  }
  return new ApiError(code, res.status, message || MESSAGES[code] || MESSAGES['internal']!);
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Header aggiuntivi, per il Content-Range dei chunk. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...options.headers };
  let body: BodyInit | undefined;

  if (method !== 'GET') {
    headers['x-csrf-token'] = readCsrfToken();
  }

  if (options.body instanceof FormData || options.body instanceof Blob) {
    body = options.body;
  } else if (options.body instanceof ArrayBuffer || options.body instanceof Uint8Array) {
    body = options.body as BodyInit;
    headers['Content-Type'] = 'application/octet-stream';
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body);
    headers['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body,
      // I cookie di sessione sono httpOnly: senza questo non vengono inviati.
      credentials: 'same-origin',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') throw err;
    throw new ApiError('network', 0, MESSAGES['network']!);
  }

  if (!res.ok) throw await toApiError(res);
  return res;
}

async function json<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await request(path, options);
  return (await res.json()) as T;
}

export const api = {
  login: (username: string, password: string) =>
    request('/api/auth/login', { method: 'POST', body: { username, password } }),

  logout: () => request('/api/auth/logout', { method: 'POST' }),

  me: () => json<Me>('/api/auth/me'),

  /** L'SSO si mostra solo se il server è configurato per farlo. */
  ssoStatus: () => json<{ enabled: boolean }>('/api/auth/sso/status'),

  switchClient: (clientId: number) =>
    json<{ activeClient: unknown }>('/api/auth/switch-client', {
      method: 'POST',
      body: { clientId },
    }),

  browse: (ref?: string) =>
    json<BrowseResult>(ref ? `/api/browse?ref=${encodeURIComponent(ref)}` : '/api/browse'),

  search: (q: string, ref?: string) =>
    json<SearchResponse>(
      `/api/search?q=${encodeURIComponent(q)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`,
    ),

  /** URL diretto: il browser scarica grazie a Content-Disposition, in streaming. */
  fileUrl: (ref: string) => `/api/file?ref=${encodeURIComponent(ref)}`,

  /**
   * Destinazione di un collegamento `.url`. Il file non si scarica: un `.url`
   * salvato si apre solo su Windows, quindi il server lo legge, ne valida lo
   * schema (solo http/https) e restituisce l'indirizzo.
   */
  link: (ref: string) => json<LinkTarget>(`/api/link?ref=${encodeURIComponent(ref)}`),

  createFolder: (parentRef: string | undefined, name: string) =>
    json<{ ref: string; name: string }>(
      parentRef ? `/api/folder?ref=${encodeURIComponent(parentRef)}` : '/api/folder',
      { method: 'POST', body: { name } },
    ),

  deleteNode: (ref: string, kind: 'folder' | 'file') =>
    request(`/api/node?ref=${encodeURIComponent(ref)}&kind=${kind}`, { method: 'DELETE' }),

  openUploadSession: (dirRef: string | undefined, filename: string, size: number) =>
    json<UploadSessionInfo>(
      dirRef
        ? `/api/upload/session?ref=${encodeURIComponent(dirRef)}`
        : '/api/upload/session',
      { method: 'POST', body: { filename, size } },
    ),

  putChunk: (
    uploadId: string,
    chunk: Uint8Array,
    start: number,
    end: number,
    total: number,
    signal?: AbortSignal,
  ) =>
    json<{ done: boolean; uploadedBytes?: number; name?: string }>(
      `/api/upload/session/${uploadId}`,
      {
        method: 'PUT',
        body: chunk,
        headers: { 'Content-Range': `bytes ${start}-${end}/${total}` },
        ...(signal ? { signal } : {}),
      },
    ),
};

/**
 * Upload semplice via XHR e non fetch: serve `upload.onprogress`, che fetch non
 * espone. Per un file da 4 MB su una linea lenta la barra di avanzamento non è
 * un lusso.
 */
export function uploadSmall(
  dirRef: string | undefined,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ name: string; renamed: boolean }> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    const url = dirRef ? `/api/upload?ref=${encodeURIComponent(dirRef)}` : '/api/upload';
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-csrf-token', readCsrfToken());

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { name: string; renamed: boolean };
          resolve(body);
        } catch {
          resolve({ name: file.name, renamed: false });
        }
        return;
      }
      let code = 'internal';
      let message = '';
      try {
        const body = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } };
        code = body.error?.code ?? code;
        message = body.error?.message ?? '';
      } catch {
        // niente da estrarre
      }
      reject(new ApiError(code, xhr.status, message || MESSAGES[code] || MESSAGES['internal']!));
    };

    xhr.onerror = () => reject(new ApiError('network', 0, MESSAGES['network']!));
    xhr.onabort = () => reject(Object.assign(new Error('annullato'), { name: 'AbortError' }));

    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

export const admin = {
  clients: () => json<AdminClientsResult>('/api/admin/clients'),

  createClient: (input: ClientInput) =>
    json<AdminClient>('/api/admin/clients', { method: 'POST', body: input }),

  updateClient: (id: number, patch: Partial<ClientInput>) =>
    json<AdminClient>(`/api/admin/clients/${id}`, { method: 'PATCH', body: patch }),

  deactivateClient: (id: number) =>
    json<AdminClient>(`/api/admin/clients/${id}`, { method: 'DELETE' }),

  folders: (path: string) =>
    json<FoldersResult>(`/api/admin/folders?path=${encodeURIComponent(path)}`),

  clientUsers: (id: number) => json<OrgUsersResult>(`/api/admin/clients/${id}/users`),

  users: (q: string, filtro: 'tutti' | 'interni' | 'admin') =>
    json<PortalUsersResult>(
      `/api/admin/users?filtro=${filtro}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    ),

  updateUser: (id: number, patch: { isAdmin?: boolean; isActive?: boolean }) =>
    json<PortalUsersResult['users'][number]>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: patch,
    }),

  resolveUser: (username: string) =>
    json<ResolveUserResult>(`/api/admin/resolve-user?username=${encodeURIComponent(username)}`),
};

/**
 * I filtri diventano querystring in un posto solo: elenco ed export **devono**
 * mandare gli stessi parametri, altrimenti il CSV scaricato non corrisponde a
 * quello che si stava guardando.
 */
export function auditQuery(f: Partial<AuditFilters>): URLSearchParams {
  const q = new URLSearchParams();
  if (f.azione?.length) q.set('azione', f.azione.join(','));
  if (f.esito?.length) q.set('esito', f.esito.join(','));
  if (f.utente?.trim()) q.set('utente', f.utente.trim());
  if (f.organizzazione != null) q.set('organizzazione', String(f.organizzazione));
  if (f.percorso?.trim()) q.set('percorso', f.percorso.trim());
  if (f.richiesta?.trim()) q.set('richiesta', f.richiesta.trim());
  if (f.da) q.set('da', f.da);
  if (f.a) q.set('a', f.a);
  return q;
}

export const logs = {
  meta: () => json<AuditMeta>('/api/admin/audit/meta'),

  page: (f: Partial<AuditFilters>, cursore: string | null, limite: number) => {
    const q = auditQuery(f);
    q.set('limite', String(limite));
    if (cursore) q.set('cursore', cursore);
    return json<AuditPage>(`/api/admin/audit?${q.toString()}`);
  },

  /**
   * URL dell'export, non una fetch: il download lo fa il browser, che gestisce
   * da se' `Content-Disposition` e non tiene il CSV in memoria come blob.
   * Conseguenza da accettare: l'header `X-Audit-Troncato` non e' leggibile per
   * questa via, quindi il troncamento lo si annuncia prima, dal conteggio.
   */
  exportUrl: (f: Partial<AuditFilters>) => `/api/admin/audit/export.csv?${auditQuery(f).toString()}`,
};

export const templates = {
  list: () => json<{ templates: Template[] }>('/api/admin/templates'),

  create: (name: string, description: string | null) =>
    json<Template>('/api/admin/templates', { method: 'POST', body: { name, description } }),

  rename: (id: number, name: string, description: string | null) =>
    json<Template>(`/api/admin/templates/${id}`, { method: 'PATCH', body: { name, description } }),

  remove: (id: number) => request(`/api/admin/templates/${id}`, { method: 'DELETE' }),

  rules: (id: number) => json<{ rules: Rule[] }>(`/api/admin/templates/${id}/rules`),

  addRule: (templateId: number, rule: RuleInput) =>
    json<{ id: number }>(`/api/admin/templates/${templateId}/rules`, { method: 'POST', body: rule }),

  updateRule: (ruleId: number, rule: RuleInput) =>
    json<{ id: number }>(`/api/admin/rules/${ruleId}`, { method: 'PATCH', body: rule }),

  deleteRule: (ruleId: number) => request(`/api/admin/rules/${ruleId}`, { method: 'DELETE' }),

  /** Dry-run: nessuna modifica, solo il confronto prima/dopo (§6.5). */
  preview: (body: PreviewBody) =>
    json<PreviewResult>('/api/admin/rules/preview', { method: 'POST', body }),
};

/**
 * Corpo del dry-run. Il bersaglio cambia con lo scope: un template si prova su
 * piu' organizzazioni, una deroga di organizzazione o di utenza su quella sola.
 */
export type PreviewBody = {
  rule: RuleInput | null;
  ruleId: number | null;
  /** true = simula l'eliminazione di `ruleId`. */
  remove?: boolean;
  scope?: 'template' | 'client' | 'user';
  templateId?: number;
  clientIds?: number[];
  clientId?: number;
  userId?: number;
  maxDepth?: number;
};

/**
 * Regole scritte sotto il template: la deroga per una singola organizzazione e
 * quella per una singola utenza dentro l'organizzazione.
 *
 * Modifica ed eliminazione passano per `templates.updateRule` /
 * `templates.deleteRule`: sono endpoint per id, uguali per tutti gli scope.
 */
export const scopedRules = {
  forClient: (clientId: number) =>
    json<ClientRulesResult>(`/api/admin/clients/${clientId}/rules`),

  addForClient: (clientId: number, rule: RuleInput) =>
    json<Rule>(`/api/admin/clients/${clientId}/rules`, { method: 'POST', body: rule }),

  forUser: (clientId: number, userId: number) =>
    json<UserRulesResult>(`/api/admin/clients/${clientId}/users/${userId}/rules`),

  addForUser: (clientId: number, userId: number, rule: RuleInput) =>
    json<Rule>(`/api/admin/clients/${clientId}/users/${userId}/rules`, {
      method: 'POST',
      body: rule,
    }),
};

export const SMALL_UPLOAD_LIMIT = 4 * 1024 * 1024;
